// New crew invite modal - Figma design with tabs (Single Invite / Bulk Import)
// Reuses same EmployeeService.create API, simplified fields (no idType/idNumber)
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Send, UserPlus, Upload, Mail } from 'lucide-react';
import { ErrorToast } from 'components';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from 'components/ui/dialog';
import EmployeeService from 'api/employees';
import { cn } from 'lib/utils';
import CrewBulkImportTab from './crew-bulk-import-tab';

const TABS = [
  { id: 'single', label: 'Single Invite', icon: UserPlus },
  { id: 'bulk', label: 'Bulk Import (CSV)', icon: Upload },
];

const CrewInviteModal = ({
  open,
  onClose,
  member,
  fetchEmployees,
  departments,
  onDelete,
  isDeleting,
}) => {
  const [activeTab, setActiveTab] = useState('single');
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: member?.firstName || '',
    lastName: member?.lastName || '',
    email: member?.email || '',
    designation: member?.designation || '',
    department: member?.departments?.[0] || '',
  });

  const isEdit = !!member;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) return;

    try {
      setIsSaving(true);
      const clientId = localStorage.getItem('ROOT_ID');
      const payload = {
        clientId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        designation: form.designation,
        departments: form.department ? [form.department] : [],
      };

      if (isEdit) await EmployeeService.update(member._id, payload);
      else await EmployeeService.create(payload);

      await fetchEmployees();
      onClose();
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={cn('sm:tw-max-w-[520px]', activeTab === 'bulk' && !isEdit && 'sm:tw-max-w-[780px]')}>
        <DialogHeader>
          <DialogTitle className="tw-text-xl tw-font-semibold">
            {isEdit ? 'Edit Member' : 'Invite Team Members'}
          </DialogTitle>
          <DialogDescription className="tw-sr-only">
            {isEdit ? 'Edit member details' : 'Invite a new team member or bulk import via CSV'}
          </DialogDescription>
        </DialogHeader>

        {/* Tabs - only show for new invite, not edit */}
        {!isEdit && (
          <div className="tw-flex tw-border-b tw-border-gray-200">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-border-0 tw-border-b-2 tw-cursor-pointer tw-bg-transparent tw-transition-colors',
                    activeTab === tab.id
                      ? 'tw-border-indigo-600 tw-text-indigo-600'
                      : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'
                  )}
                >
                  <Icon className="tw-w-4 tw-h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Single Invite Form */}
        {(activeTab === 'single' || isEdit) && (
          <form onSubmit={handleSubmit} className="tw-space-y-4">
            {/* First / Last Name row */}
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. John"
                  value={form.firstName}
                  onChange={(e) => updateField('firstName', e.target.value)}
                  required
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 tw-bg-white"
                />
              </div>
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Doe"
                  value={form.lastName}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  required
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 tw-bg-white"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                Email Address
              </label>
              <input
                type="email"
                placeholder="john.doe@company.com"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                required
                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 tw-bg-white"
              />
            </div>

            {/* Role / Department row */}
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                  Role / Designation
                </label>
                <input
                  type="text"
                  placeholder="e.g. Engineer"
                  value={form.designation}
                  onChange={(e) => updateField('designation', e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 tw-bg-white"
                />
              </div>
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                  Department
                </label>
                <select
                  value={form.department}
                  onChange={(e) => updateField('department', e.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 tw-bg-white tw-appearance-none"
                >
                  <option value="">Select Dept</option>
                  {departments?.map((dept) => (
                    <option key={dept._id} value={dept._id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Info banner */}
            <div className="tw-flex tw-items-start tw-gap-2 tw-bg-indigo-50 tw-border tw-border-indigo-100 tw-rounded-lg tw-px-3 tw-py-2.5">
              <Mail className="tw-w-4 tw-h-4 tw-text-indigo-600 tw-mt-0.5 tw-flex-shrink-0" />
              <p className="tw-text-xs tw-text-indigo-700 tw-m-0">
                The user will receive an email invitation to set up their account and complete their profile details.
              </p>
            </div>

            {/* Footer buttons */}
            <DialogFooter className="tw-pt-2">
              {isEdit && onDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(member)}
                  disabled={isDeleting}
                  className="tw-mr-auto tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-red-600 tw-bg-transparent tw-border tw-border-red-200 tw-rounded-lg tw-cursor-pointer hover:tw-bg-red-50 disabled:tw-opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-transparent tw-border tw-border-gray-300 tw-rounded-lg tw-cursor-pointer hover:tw-bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-indigo-600 tw-border-0 tw-rounded-lg tw-cursor-pointer hover:tw-bg-indigo-700 disabled:tw-opacity-50"
              >
                <Send className="tw-w-4 tw-h-4" />
                {isSaving ? 'Sending...' : isEdit ? 'Save Changes' : 'Send Invitation'}
              </button>
            </DialogFooter>
          </form>
        )}

        {/* Bulk Import Tab */}
        {activeTab === 'bulk' && !isEdit && (
          <CrewBulkImportTab fetchEmployees={fetchEmployees} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CrewInviteModal;
